import { useState } from 'react';
import { Flex, Text, Skeleton, Icon, iconWeight, Button } from '@nula/core-ui';
import { usePlanCatalog } from './use-subscription';
import { PlanCatalogCard } from './plan-catalog-card';
import { IntervalToggle } from './interval-toggle';
import { PromoCodeInput, type AppliedPromo, type CheckoutRejection } from './promo-code-input';
import type { BillingInterval, PlanCatalogEntry, SubscriptionStatus } from './types';
import styles from './plan-catalog.module.css';

interface PlanCatalogProps {
  subscription: SubscriptionStatus;
  onSelectPlan: (plan: PlanCatalogEntry, interval: BillingInterval, promotionCode?: string) => void;
  /** When provided, shows a back affordance (used when a paid user opens the catalog to change plans). */
  onBack?: () => void;
  /** Set by the parent when checkout itself rejects the applied code (Stripe's second validation). */
  checkoutRejection?: CheckoutRejection | null;
  /**
   * Workspace the promo preview must validate against — must match whatever
   * workspace checkout will actually charge. Omit only when there is no
   * correct workspace to pin yet (e.g. team creation, which checks out
   * against a workspace that doesn't exist until after submit).
   */
  workspaceId?: string;
}

export function PlanCatalog({ subscription, onSelectPlan, onBack, checkoutRejection, workspaceId }: PlanCatalogProps) {
  const { data, isLoading } = usePlanCatalog(subscription.workspaceType);
  const [interval, setInterval] = useState<BillingInterval>('year');
  const [appliedPromo, setAppliedPromo] = useState<AppliedPromo | null>(null);

  const plans = data?.plans ?? [];
  const includesFor = (index: number) =>
    index > 0 ? `Everything in ${plans[index - 1].name}, and:` : undefined;

  if (isLoading) {
    return <Skeleton active data-testid="plan-catalog-skeleton" />;
  }

  return (
    <Flex vertical gap="md">
      {onBack && (
        <Button
          variant="text"
          size="sm"
          icon={<Icon name="arrow_back" size={16} weight={iconWeight.regular} />}
          className={styles.back}
          onClick={onBack}
        >
          Back
        </Button>
      )}

      <IntervalToggle value={interval} onChange={setInterval} />

      <PromoCodeInput
        interval={interval}
        workspaceId={workspaceId}
        onApplied={setAppliedPromo}
        onRemoved={() => setAppliedPromo(null)}
        checkoutRejection={checkoutRejection}
        rejectReferrals={subscription.workspaceType === 'team'}
      />

      <div className={styles.grid}>
        {plans.map((plan, i) => (
          <PlanCatalogCard
            key={plan.code}
            plan={plan}
            subscription={subscription}
            interval={interval}
            includesLabel={includesFor(i)}
            recommended={plan.code === 'pro'}
            appliedPromo={appliedPromo}
            onSelect={(p) => onSelectPlan(p, interval, appliedPromo?.code)}
          />
        ))}
      </div>

      <Text size="xs" type="secondary" className={styles.footnote}>
        Prices billed per workspace · cancel anytime
      </Text>
    </Flex>
  );
}
